import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'primary' | 'danger' | 'warning';
  /** Optioneel: tekst die de gebruiker exact moet typen (bv. productcode) */
  requireText?: string;
}

/** Breedtegrenzen van het chatpaneel (px); het maximum volgt ook het venster (max. 60 %). */
export const CHAT_MIN_WIDTH = 300;
export const CHAT_MAX_WIDTH = 900;
export const CHAT_DEFAULT_WIDTH = 400;

export function clampChatWidth(width: number, viewportWidth = window.innerWidth): number {
  const max = Math.max(CHAT_MIN_WIDTH, Math.min(CHAT_MAX_WIDTH, Math.floor(viewportWidth * 0.6)));
  const w = Number.isFinite(width) && width > 0 ? Math.round(width) : CHAT_DEFAULT_WIDTH;
  return Math.min(max, Math.max(CHAT_MIN_WIDTH, w));
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* privémodus of vol — dan alleen sessiestate */
  }
}

export const useUiStore = defineStore('ui', () => {
  const sidebarCollapsed = ref(readStorage('kl.sidebarCollapsed') === '1');
  const sidebarMobileOpen = ref(false);

  function toggleSidebar() {
    sidebarCollapsed.value = !sidebarCollapsed.value;
    writeStorage('kl.sidebarCollapsed', sidebarCollapsed.value ? '1' : '0');
  }

  // Chat-assistent (rechterkolom): open/dicht en breedte, lokaal persistent
  const chatOpen = ref(readStorage('kl.chatOpen') === '1');
  const chatWidth = ref(clampChatWidth(Number(readStorage('kl.chatWidth')) || CHAT_DEFAULT_WIDTH));

  function toggleChat(open?: boolean) {
    chatOpen.value = open ?? !chatOpen.value;
    writeStorage('kl.chatOpen', chatOpen.value ? '1' : '0');
  }

  /** Breedte zetten; `persist=false` tijdens het slepen (pas bij loslaten opslaan). */
  function setChatWidth(width: number, persist = true) {
    chatWidth.value = clampChatWidth(width);
    if (persist) writeStorage('kl.chatWidth', String(chatWidth.value));
  }

  // Globale confirm-modal
  const confirmState = ref<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);
  function confirm(opts: ConfirmOptions): Promise<boolean> {
    return new Promise((resolve) => {
      confirmState.value = {
        confirmLabel: 'Bevestigen',
        cancelLabel: 'Annuleren',
        variant: 'primary',
        ...opts,
        resolve,
      };
    });
  }
  function resolveConfirm(value: boolean) {
    confirmState.value?.resolve(value);
    confirmState.value = null;
  }

  return {
    sidebarCollapsed,
    sidebarMobileOpen,
    toggleSidebar,
    chatOpen,
    chatWidth,
    toggleChat,
    setChatWidth,
    confirmState,
    confirm,
    resolveConfirm,
  };
});
